import { Checkbox } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';

import type {
  CascadeEntityType,
  CascadeMode,
  CascadeScope,
} from '../../hooks/useCascadeSelections';
import './CascadeMenu.scss';

type CascadeMenuProps<TType extends CascadeEntityType> = {
  entityType: TType;
  mode: CascadeMode;
  scopes: CascadeScope<TType>[];
  selections: Record<CascadeScope<TType>, boolean>;
  labels: Record<CascadeScope<TType>, ReactNode>;
  onToggle: (scope: CascadeScope<TType>, next: boolean) => void;
  disabledScopes?: Partial<Record<CascadeScope<TType>, boolean>>;
  applyLabel: string;
  onApply: () => void;
  onCancel?: () => void;
  applyDisabled?: boolean;
};

const SCOPE_ORDER_PRIORITY: Record<CascadeEntityType, CascadeScope<CascadeEntityType>[]> = {
  album: ['album', 'recordings', 'tracks'],
  recording: ['recording', 'tracks'],
  playlist: ['playlist', 'recordings', 'tracks'],
  work: ['work', 'recordings'],
};

const sortScopes = <TType extends CascadeEntityType>(
  entityType: TType,
  scopes: CascadeScope<TType>[]
): CascadeScope<TType>[] => {
  const order = SCOPE_ORDER_PRIORITY[
    entityType
  ] as CascadeScope<TType>[];
  return [...scopes].sort((a, b) => order.indexOf(a) - order.indexOf(b));
};

export const CascadeMenu = <TType extends CascadeEntityType>(
  props: CascadeMenuProps<TType>
) => {
  const {
    entityType,
    scopes,
    selections,
    labels,
    onToggle,
    disabledScopes,
    applyLabel,
    onApply,
    onCancel,
    applyDisabled,
  } = props;

  const sortedScopes = sortScopes(entityType, scopes);

  return (
    <div
      className='cascade-menu'
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className='cascade-menu__options'>
        {sortedScopes.map((scope) => {
          const disabled = disabledScopes?.[scope] ?? false;
          const checked = selections[scope] ?? false;
          return (
            <button
              key={scope}
              type='button'
              className={classNames('cascade-menu__option', {
                'cascade-menu__option--disabled': disabled,
              })}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (disabled) return;
                onToggle(scope, !checked);
              }}
            >
              <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (disabled) return;
                  onToggle(scope, event.target.checked);
                }}
              />
              <span className='cascade-menu__label'>{labels[scope]}</span>
            </button>
          );
        })}
      </div>
      <div className='cascade-menu__footer'>
        {onCancel ? (
          <button
            type='button'
            className='cascade-menu__cancel'
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onCancel();
            }}
          >
            Cancel
          </button>
        ) : null}
        <button
          type='button'
          className='cascade-menu__apply'
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (applyDisabled) return;
            onApply();
          }}
          disabled={applyDisabled}
        >
          {applyLabel}
        </button>
      </div>
    </div>
  );
};

CascadeMenu.displayName = 'CascadeMenu';
